/**
 * OS language detection via the native bridge (`window.nativeDevice.getOsLanguage()`).
 *
 * Used ONLY as the default UI language on first boot — a stored choice or a server-side
 * preference always wins. Call `applyOsLanguageDefault()` once when the SPA boots.
 */

import Cookies from "js-cookie";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, SupportedLanguage } from "@/constants/languages";
import { readStoredLanguage } from "@/utils/language-storage";
import { sleep } from "@/utils/pieces";
import i18nInstance from "@/i18n";

export type OsLanguageInfo = {
  /** ISO 639, lowercase */
  languageCode: string;
  /** ISO 3166, uppercase. Null when the device has no region set */
  countryCode: string | null;
  /** Full BCP-47 tag */
  tag: string;
  /** "fallback" = native could not read the device language, defaulted to Japanese */
  source: "device" | "fallback";
};

const RETRY_COUNT = 3;
const RETRY_DELAY_MS = 200;

/**
 * Read the device language from native. Retries — the bridge may not be ready at SPA boot.
 *
 * @returns Device language info, or null outside the native webview / when the bridge stays silent
 */
export async function getOsLanguage(retries = RETRY_COUNT, delayMs = RETRY_DELAY_MS): Promise<OsLanguageInfo | null> {
  // Plain browser → bridge is undefined
  if (!window.nativeDevice?.getOsLanguage) return null;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const info = await window.nativeDevice.getOsLanguage();
      if (info?.languageCode) return info;
    } catch {
      // Bridge not ready — retry
    }

    if (attempt < retries - 1) {
      await sleep(delayMs);
    }
  }

  return null;
}

/**
 * Map a device language onto a supported one; unsupported codes → DEFAULT_LANGUAGE.
 *
 * Matches on `languageCode`, not `tag`: Language and Region are set independently on iOS/Android,
 * so the tag can be e.g. "en-VN" or just "en".
 */
export function resolveOsLanguage(info: OsLanguageInfo | null): SupportedLanguage {
  const code = info?.languageCode?.toLowerCase();

  return SUPPORTED_LANGUAGES.includes(code as SupportedLanguage) ? (code as SupportedLanguage) : DEFAULT_LANGUAGE;
}

/**
 * Sync check of whether applyOsLanguageDefault() will do anything on this boot.
 *
 * False in a plain browser, and false once the user has a language of their own: an explicit
 * choice in localStorage, or a session whose server-side preference wins.
 */
export function shouldApplyOsLanguage(): boolean {
  if (!window.nativeDevice?.getOsLanguage) return false;

  return !readStoredLanguage() && !Cookies.get("learner-token");
}

/**
 * Apply the device language as the initial UI language.
 *
 * NOT persisted — re-derived on every boot until the user picks a language themselves.
 *
 * @returns The applied language, or null when nothing was applied
 */
export async function applyOsLanguageDefault(): Promise<SupportedLanguage | null> {
  if (!shouldApplyOsLanguage()) return null;

  const info = await getOsLanguage();
  if (!info) return null;

  const language = resolveOsLanguage(info);
  await i18nInstance.changeLanguage(language);

  return language;
}
